import actionTypes from './types'

/**
 * Loaded laps and the contest they belong to
 * @type {{ last_id: number, contest: Object|null, rows: Array<Object> }}
 */
const INITIAL_STATE = {
  last_id: 0, // highest lap id loaded; the next request asks for laps after it
  contest: null, // contest of the rows
  rows: [] // data rows
}

/**
 * Get the rows from state, also from old style state where data contained the rows
 * @param {Object|Array} state current state
 * @returns {Array<Object>} rows
 */
const getStateRows = ( state ) =>
{
  if ( Array.isArray( state ) ) return state
  const { rows = [] } = state || {}
  return Array.isArray( rows ) ? rows : []
}

/**
 * Get the lap count per car from rows
 * @param {Array<Object>} rows laps
 * @returns {Object<string, number>} highest lap number per car
 */
const getLapCounts = ( rows ) =>
{
  const laps = {}
  rows.forEach( row =>
  {
    laps[ row.car ] = Math.max( laps[ row.car ] || 0, row.lap || 0 )
  } )
  return laps
}

/**
 * Add new laps; laps of another contest replace the current ones
 * @param {Object} state current state
 * @param {Object} action action with rows and contest
 * @returns {Object} new state
 */
const setData = ( state, action ) =>
{
  const { rows: payload = [], contest = null } = action.payload || {}
  if ( !Array.isArray( payload ) ) return state

  // get rows from state, unless the laps are of another contest
  const old_contest = state && state.contest ? state.contest.id : 0
  const same_contest = !contest || !old_contest || contest.id === old_contest
  let old_rows = same_contest ? getStateRows( state ) : []

  // laps up to this id are loaded already
  const loaded_id = same_contest && state && state.last_id ? state.last_id : 0

  let last_id = loaded_id,
      last_car = '',
      last_time = '',
      // use recent flag on rows? only if there was data before
      use_recent = state && state.length,
      // new rows to be added
      rows = []

  // sort on timestamp to check for duplicates
  payload.sort( ( a, b ) => a.time < b.time ? 1 : -1 )

  // add missing data
  for( const row of payload )
  {
    // skip laps that are loaded already, should the API send them again
    if ( row.id <= loaded_id ) continue

    // get highest lap id
    if ( row.id > last_id )
    {
      last_id = row.id
    }

    // position is to be determined in relation the group later
    row.pos = '-'

    // time difference with the previous fastest time
    row.diff = '-'

    // make sure penalty exists and is a number
    if ( !row.hasOwnProperty( 'penalty' ) || !row.penalty )
    {
      row.penalty = 0
    }
    else
    {
      row.penalty = parseInt( row.penalty )
    }

    // make sure comment exists
    if ( !row.hasOwnProperty( 'comment' ) || !row.comment )
    {
      row.comment = ''
    }
    else
    {
      row.comment = row.comment.trim()
    }

    // remove duplicates in the payload: the same car with the same laptime twice in a row
    if ( last_car !== parseInt( row.car ) || last_time !== String( row.laptime ) )
    {
      // store last car and time to check for duplicates
      last_car = parseInt( row.car )
      last_time = String( row.laptime )
      // mark as recently added if there was data before
      row.recent = use_recent
      rows.push( row )
    }
  }

  // add lap number per car, continuing from the loaded laps
  const laps = getLapCounts( old_rows )
  rows.reverse()
  rows.forEach( row =>
  {
    laps[ row.car ] = ( laps[ row.car ] || 0 ) + 1
    row.lap = laps[ row.car ]
  } )

  // remove recent flag from all previous rows
  old_rows = old_rows.map( row => {
    row.recent = false
    return row
  } )

  // merge
  return {
    last_id,
    contest: contest || ( state && state.contest ) || null,
    rows: [
      ...old_rows,
      ...rows
    ]
  }
}

/**
 * Reset all car data
 * @returns {Object} empty state
 */
const resetData = () => INITIAL_STATE

/**
 * Reduce data
 * @param {Object} state current state
 * @param {Object} action dispatched action
 * @returns {Object} new state
 */
const dataReducer = ( state = INITIAL_STATE, action ) =>
{
  switch ( action.type )
  {
    case actionTypes.RESET_DATA:
      return resetData()

    case actionTypes.SET_DATA:
      return setData( state, action )

    default:
      return state;
  }
}

export default dataReducer
